/**
 * NotificationBell — header notification inbox.
 *
 * Renders a bell button with an unread-count badge. Clicking opens an OffCanvas
 * listing the caller's notifications (newest first). Each row is rendered
 * generically from a per-type descriptor (see `notificationTypes.ts`), so
 * adding a new notification kind never requires editing this component.
 *
 * Polling cadence: lightweight unread-count poll every 60s while mounted; the
 * full list is fetched lazily when the panel is opened.
 *
 * @userstory US-NOTIF-02, US-NOTIF-03
 * @userstory US-MON-10 — surfaces "monitor.talent_available" notifications
 */
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button, { IButtonProps } from '../bootstrap/Button';
import Icon from '../icon/Icon';
import { otIconFor } from '../icon/svg-icons/ot/otIconFor';
import OffCanvas, { OffCanvasBody, OffCanvasHeader, OffCanvasTitle } from '../bootstrap/OffCanvas';
import Popovers from '../bootstrap/Popovers';
import Spinner from '../bootstrap/Spinner';
import AuthContext from '../../contexts/authContext';
import { UserType } from '../../common/data/dummyRolesData';
import useDarkMode from '../../hooks/useDarkMode';
import notificationsService, { Notification } from '../../services/api/notificationsService';
import { resolveNotificationDescriptor } from './notificationTypes';

interface INotificationBellProps {
	styledBtn: IButtonProps;
}

const POLL_INTERVAL_MS = 60_000; // 60s — mirrors authContext.tsx cadence

function formatRelative(iso: string, lang: string): string {
	try {
		const d = new Date(iso);
		const diffMs = Date.now() - d.getTime();
		const diffMin = Math.round(diffMs / 60000);
		const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
		if (diffMin < 1) return rtf.format(0, 'minute');
		if (diffMin < 60) return rtf.format(-diffMin, 'minute');
		const diffHr = Math.round(diffMin / 60);
		if (diffHr < 24) return rtf.format(-diffHr, 'hour');
		const diffDay = Math.round(diffHr / 24);
		return rtf.format(-diffDay, 'day');
	} catch {
		return iso;
	}
}

const NotificationBell: FC<INotificationBellProps> = ({ styledBtn }) => {
	const { t, i18n } = useTranslation('notifications');
	const navigate = useNavigate();
	const { userData } = useContext(AuthContext);
	const { darkModeStatus } = useDarkMode();
	const role: UserType | null = (userData?.role as UserType | undefined) ?? null;

	// Theme-aware background classes for notification rows. Mirrors the convention
	// used elsewhere in the app (e.g. TalentPeerSearchView, PresentationCard).
	const readBgClass = darkModeStatus ? 'bg-dark bg-opacity-50' : 'bg-light';
	const unreadBgClass = darkModeStatus ? 'bg-lo25-primary' : 'bg-l25-primary';

	const [unreadCount, setUnreadCount] = useState<number>(0);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [items, setItems] = useState<Notification[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const fetchUnreadCount = useCallback(async () => {
		try {
			const count = await notificationsService.getUnreadCount();
			setUnreadCount(count);
		} catch {
			// Silent — bell badge is non-critical. Auth errors handled centrally.
		}
	}, []);

	const fetchList = useCallback(async () => {
		setIsLoading(true);
		try {
			const list = await notificationsService.getMyNotifications();
			setItems(list);
		} catch {
			setItems([]);
		} finally {
			setIsLoading(false);
		}
	}, []);

	// Initial fetch + polling for the unread count.
	useEffect(() => {
		fetchUnreadCount();
		const id = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
		return () => clearInterval(id);
	}, [fetchUnreadCount]);

	// Lazy-load the full list when the panel opens; refresh unread count on close.
	useEffect(() => {
		if (isOpen) {
			fetchList();
		} else {
			fetchUnreadCount();
		}
	}, [isOpen, fetchList, fetchUnreadCount]);

	const handleRowClick = useCallback(
		async (n: Notification) => {
			const descriptor = resolveNotificationDescriptor(n.type);
			const route = descriptor.buildRoute?.({ data: n.data ?? {}, role }) ?? null;

			if (!n.isRead) {
				try {
					await notificationsService.markAsRead(n.id);
					setItems((prev) =>
						prev.map((p) =>
							p.id === n.id
								? { ...p, isRead: true, readAt: new Date().toISOString() }
								: p,
						),
					);
					setUnreadCount((c) => Math.max(0, c - 1));
				} catch {
					// Non-blocking: still allow navigation even if mark-as-read fails.
				}
			}
			if (route) {
				setIsOpen(false);
				navigate(route);
			}
		},
		[navigate, role],
	);

	const handleMarkAllAsRead = useCallback(async () => {
		const unread = items.filter((n) => !n.isRead);
		if (unread.length === 0) return;
		await Promise.allSettled(unread.map((n) => notificationsService.markAsRead(n.id)));
		setItems((prev) =>
			prev.map((p) =>
				p.isRead ? p : { ...p, isRead: true, readAt: new Date().toISOString() },
			),
		);
		setUnreadCount(0);
	}, [items]);

	const badgeLabel = useMemo(
		() => (unreadCount > 99 ? '99+' : String(unreadCount)),
		[unreadCount],
	);

	return (
		<>
			<div className='col-auto'>
				{/* Tight position-relative wrapper sized to the button so the badge
				    sits on the bell's top-right corner — not on the column edge. */}
				<div className='position-relative d-inline-block'>
					<Popovers trigger='hover' desc={t('bell.tooltip')}>
						<Button
							/* eslint-disable-next-line react/jsx-props-no-spreading */
							{...styledBtn}
							icon='OtNotifications'
							size='lg'
							onClick={() => setIsOpen(true)}
							aria-label={t('bell.ariaLabel')}
						/>
					</Popovers>
					{unreadCount > 0 && (
						<span
							className='position-absolute badge rounded-pill bg-danger'
							style={{
								top: 2,
								right: 2,
								fontSize: '0.6rem',
								lineHeight: 1,
								padding: '0.2rem 0.35rem',
								zIndex: 1,
								pointerEvents: 'none',
							}}
							aria-label={t('bell.unreadAria', { count: unreadCount })}>
							{badgeLabel}
						</span>
					)}
				</div>
			</div>

			<OffCanvas
				id='ota-notification-bell-canvas'
				titleId='ota-notification-bell-title'
				placement='end'
				isOpen={isOpen}
				setOpen={setIsOpen}>
				<OffCanvasHeader setOpen={setIsOpen}>
					<OffCanvasTitle id='ota-notification-bell-title'>
						{t('panel.title')}
					</OffCanvasTitle>
				</OffCanvasHeader>
				<OffCanvasBody>
					{isLoading && (
						<div className='d-flex justify-content-center py-4'>
							<Spinner color='primary' />
						</div>
					)}

					{!isLoading && items.length === 0 && (
						<div className='text-center text-muted py-4'>
							<Icon
								icon={otIconFor('NotificationsNone')}
								size='3x'
								className='mb-2 d-block mx-auto'
							/>
							<div>{t('panel.empty')}</div>
						</div>
					)}

					{!isLoading && items.length > 0 && (
						<>
							<div className='d-flex justify-content-end mb-2'>
								<Button
									color='link'
									size='sm'
									onClick={handleMarkAllAsRead}
									isDisable={unreadCount === 0}>
									{t('panel.markAllRead')}
								</Button>
							</div>
							<ul className='list-unstyled mb-0'>
								{items.map((n) => {
									const descriptor = resolveNotificationDescriptor(n.type);
									const data = n.data ?? {};
									// Spread `data` as merge vars so any {{key}} in the i18n
									// string is filled from the notification payload. We also
									// pass `type` so the generic fallback can show it.
									const mergeVars = { type: n.type, ...data };
									const line = t(descriptor.titleKey, mergeVars);
									const rawBody = descriptor.bodyKey
										? t(descriptor.bodyKey, mergeVars)
										: null;
									// Hide the body line when any merge variable is missing
									// from the payload (older rows may not include all keys).
									const body =
										rawBody && !rawBody.includes('{{') ? rawBody : null;
									const route = descriptor.buildRoute?.({ data, role }) ?? null;
									const when = formatRelative(n.createdAt, i18n.language);
									const clickable = !!route || !n.isRead;
									return (
										<li
											key={n.id}
											className={`p-3 mb-2 rounded border text-body ${
												n.isRead ? readBgClass : unreadBgClass
											} ${clickable ? 'cursor-pointer' : ''}`}
											onClick={
												clickable ? () => handleRowClick(n) : undefined
											}
											onKeyDown={
												clickable
													? (e) => {
															if (
																e.key === 'Enter' ||
																e.key === ' '
															) {
																e.preventDefault();
																handleRowClick(n);
															}
														}
													: undefined
											}
											role={clickable ? 'button' : undefined}
											tabIndex={clickable ? 0 : undefined}>
											<div className='d-flex align-items-start gap-2'>
												<Icon
													icon={descriptor.icon}
													size='lg'
													color={descriptor.color ?? 'primary'}
												/>
												<div className='flex-grow-1'>
													<div className={`${n.isRead ? '' : 'fw-bold'}`}>
														{line}
													</div>
													{body && (
														<div className='text-muted small fst-italic mt-1'>
															{body}
														</div>
													)}
													<small className='text-muted'>{when}</small>
												</div>
												{!n.isRead && (
													<span
														className='rounded-circle bg-danger'
														style={{
															width: 8,
															height: 8,
															marginTop: 6,
														}}
														aria-label={t('panel.unreadDot')}
													/>
												)}
											</div>
										</li>
									);
								})}
							</ul>
						</>
					)}
				</OffCanvasBody>
			</OffCanvas>
		</>
	);
};

export default NotificationBell;
